/**
 * Sentry Vue 真接生产 (v19.0-Y)
 * 复用 v18.9.78 DSN · 整合 BrowserTracing + Replay (默认关闭)
 * 由 main.ts initSentry(app, router) 在 app.mount 前调用
 */
import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'

export function initSentry(app: App, router: Router) {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) {
    // 降级 info 不显 warning · 灯塔阶段 Sentry 数据量太少不值得配
    console.info('[v19 Sentry] 未配置 DSN · 跳过 (灯塔阶段正常)')
    return
  }

  Sentry.init({
    app,
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: `v19@${import.meta.env.VITE_VERSION || '19.0.0-alpha'}+${import.meta.env.VITE_COMMIT_SHA || 'dev'}`,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false, // 业务文本可见 (车间不敏感)
        blockAllMedia: true, // 媒体 (照片/视频) 屏蔽
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACE_SAMPLE_RATE) || 0.1,
    // Session Replay
    replaysSessionSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAY_SAMPLE_RATE) || 0.0,
    replaysOnErrorSampleRate: 1.0, // 错误时 100% replay
    // 过滤本地开发 + 测试
    beforeSend(event, hint) {
      if (import.meta.env.MODE === 'development') return null
      const err = hint.originalException as any
      // 忽略 ResizeObserver 噪音
      if (err?.message?.includes('ResizeObserver loop')) return null
      // 忽略 Sentry 自身误报 (R10 历史)
      if (err?.message?.includes('Non-Error promise rejection')) return null
      return event
    },
    // 自动 breadcrumb (业务追溯)
    initialScope: {
      tags: {
        app: 'trungnam-mes-v19',
        edition: 'standard',
      },
    },
  })

  console.info('[v19 Sentry] 已激活', {
    env: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    release: import.meta.env.VITE_VERSION,
  })
}

/**
 * 业务侧手动上报 (含 user context)
 */
export function captureException(err: Error, extra?: Record<string, any>) {
  Sentry.captureException(err, { extra })
}

export function captureMessage(msg: string, level: Sentry.SeverityLevel = 'info', extra?: Record<string, any>) {
  Sentry.captureMessage(msg, { level, extra })
}

export function setUser(user: { id: string | number; username?: string; role?: string }) {
  Sentry.setUser({
    id: String(user.id),
    username: user.username,
    role: user.role,
  } as any)
}

export function clearUser() {
  Sentry.setUser(null)
}
